import type { RecordFormField } from "features/shared/components/dynamic-form/types";
import type { DrawerOptions } from "features/shared/lib/technical-config-tab/fieldKindStrategies";
import type { TFunction } from "i18next";

import {
  buildRecordFormField,
  buildRecordFormFields,
  buildRecordFormTabs,
  type RecordFormFieldDefinition,
} from "features/shared/lib/technical-config-tab/recordFormFields";

import type { RoomCatalogFormState, RoomConfigFormState, RoomTypeCatalogItem } from "../model/types";

import {
  type CatalogHeaderFieldDefinition,
  getCatalogHeaderFieldDefinitions,
  getRoomConfigTabs,
  getTabFieldDefinitions,
  ROOM_CONFIG_DRAWER_HIDDEN_FIELD_KEYS,
  ROOM_CONFIG_TABS,
} from "./roomConfigFieldDefinitions";
import {
  isRoomSystemOptionField,
  ROOM_SYSTEM_OPTION_FIELDS,
  T_AIR_SUPPLY_TYPES,
  VENTILATION_TYPES,
} from "./roomConfigOptions";

const getFieldLabel = (t: TFunction, labelKey: string) => {
  if (labelKey === "roomTypeName" || labelKey === "color") {
    return String(t(`TechnicalData.room.columns.${labelKey}`));
  }
  return String(t(`TechnicalData.room.fields.${labelKey}`));
};

const getFieldLabelText = (field: { key: string; labelKey?: string }, t: TFunction) =>
  getFieldLabel(t, field.labelKey ?? field.key);

const buildRoomSystemFieldDrawerOptions = (
  field: RecordFormFieldDefinition,
  t: TFunction
): DrawerOptions | undefined => {
  if (field.key === "tAirSupplyType") {
    return {
      displayOptions: T_AIR_SUPPLY_TYPES.map((value) =>
        String(t(`TechnicalData.room.fields.tAirSupplyTypeOptions.${value}`))
      ),
      options: [...T_AIR_SUPPLY_TYPES],
    };
  }

  if (field.key === "ventilationType") {
    const options = [...VENTILATION_TYPES];
    return {
      displayOptions: options.map((value) => String(t(`TechnicalData.room.fields.ventilationTypeOptions.${value}`))),
      options,
      placeholder: String(t("TechnicalData.room.fields.selectVentilationType")),
    };
  }

  if (!isRoomSystemOptionField(field.key)) return undefined;

  const options = [...ROOM_SYSTEM_OPTION_FIELDS[field.key]];
  const placeholder =
    field.key === "heatingSystem"
      ? String(t("TechnicalData.room.fields.selectHeatingSystem"))
      : field.key === "coolingSystem"
      ? String(t("TechnicalData.room.fields.selectCoolingSystem"))
      : undefined;

  return { displayOptions: options, options, placeholder };
};

const ROOM_CONFIG_DRAWER_HIDDEN_FIELD_KEY_SET = new Set<string>(ROOM_CONFIG_DRAWER_HIDDEN_FIELD_KEYS);

const translateFieldSection = (section: string | undefined, t: TFunction) =>
  section ? String(t(`TechnicalData.room.otherSections.${section}`)) : undefined;

const buildCatalogHeaderField = (
  field: CatalogHeaderFieldDefinition,
  formState: RoomCatalogFormState,
  onFieldChange: (field: CatalogHeaderFieldDefinition["key"], value: unknown) => void,
  t: TFunction
): RecordFormField =>
  buildRecordFormField({
    colSpan: field.colSpan ?? 1,
    config: formState,
    filterType: field.filterType,
    inputType: field.inputType,
    key: field.key,
    label: getFieldLabelText(field, t),
    onFieldChange,
  });

type BuildRoomConfigDrawerConfigOptions = {
  catalog?: readonly RoomTypeCatalogItem[];
  formState: RoomConfigFormState;
  onFieldChange: (field: keyof RoomConfigFormState, value: unknown) => void;
  t: TFunction;
};

export const buildRoomConfigDrawerConfig = ({
  formState,
  onFieldChange,
  t,
}: BuildRoomConfigDrawerConfigOptions) => ({
  fields: buildRecordFormFields({
    definitions: getTabFieldDefinitions(),
    formState,
    getDrawerOptions: (field) => buildRoomSystemFieldDrawerOptions(field, t),
    getLabel: (field) => getFieldLabelText(field, t),
    onFieldChange,
  })
    .flatMap((field) => {
      if (field.filterName !== "hvacStartCet" && field.filterName !== "hvacEndCet") return [field];
      return [
        { ...field, tab: ROOM_CONFIG_TABS.heating },
        { ...field, tab: ROOM_CONFIG_TABS.cooling },
      ];
    })
    .map((field) => ({
      ...field,
      section: translateFieldSection(field.section, t),
      visible: ROOM_CONFIG_DRAWER_HIDDEN_FIELD_KEY_SET.has(field.filterName) ? () => false : field.visible,
    })),
  tabs: buildRecordFormTabs(getRoomConfigTabs(), t, "TechnicalData.room.tabs").map((tab) => ({
    ...tab,
    disabled: false,
    // (tab.name === ROOM_CONFIG_TABS.heating && !formState.roomHeating) ||
    // (tab.name === ROOM_CONFIG_TABS.cooling && !formState.roomCooling),
  })),
});

type BuildRoomCatalogFormConfigOptions = {
  catalog?: readonly RoomTypeCatalogItem[];
  formState: RoomCatalogFormState;
  onFieldChange: (field: keyof RoomCatalogFormState, value: unknown) => void;
  t: TFunction;
};

export const buildRoomCatalogFormConfig = ({
  catalog = [],
  formState,
  onFieldChange,
  t,
}: BuildRoomCatalogFormConfigOptions) => {
  const { fields: tabFields, tabs } = buildRoomConfigDrawerConfig({ catalog, formState, onFieldChange, t });

  const headerFields = getCatalogHeaderFieldDefinitions().map((field) =>
    buildCatalogHeaderField(field, formState, onFieldChange, t)
  );

  return {
    fields: [...headerFields, ...tabFields],
    tabs,
  };
};
